import { truncate } from 'lodash';
import dayjs from 'dayjs';
import { HOST_URL, LANG, SPONSORED } from '~/constants/config';
import { getImageUrl } from '~/modules/common/utils/imageUtils';
import { getMetaOpenGraph, getMetaTwitter, getLinkMeta } from '~/modules/common/utils/metaUtils';
import { getArticleUrl } from '~/modules/common/utils/routingUtils';
import i18n from '~/i18n';

/**
 * Checks wheter or not article has a question entry
 *
 * @param {object} article - article object
 */
export function hasQuestion(article) {
  const hasQuestionEntry = article?.entries?.some((entry) => {
    return entry.mode === 'question';
  });

  return hasQuestionEntry || article?.categories[0]?.name === 'Test';
}

/**
 * Returns article title
 *
 * @param {object} article - article object
 */
export const articleTitle = (article, isBot = false) => {
  if (article.seoData?.altTitle) {
    return article.seoData.altTitle + ' - Onedio';
  } else if (article.title) {
    return article.title + ' - Onedio';
  } else {
    return '';
  }
};

/**
 * Returns article description
 *
 * @param {object} article - article object
 */
export const articleDescription = (article) => {
  if (article.seoData?.description) {
    return truncate(article.seoData.description, { length: 160 });
  } else if (article.description) {
    return truncate(article.description, { length: 160 });
  } else {
    const category = article.categories?.[0]?.name;
    const title = articleTitle(article);
    const descriptions = {
      Test: "Sonucuna şaşıracağın $title testi Onedio'da. Çözmek için hemen tıkla!",
      Video: "Viral olan $title videosu Onedio'da. İzlemek için hemen tıkla!",
      Yemek: "Bulabileceğin en lezzetli tarifiyle $title ayrıntılı anlatımıyla Onedio'da!",
      Gündem: "Herkesin konuştuğu $title haberi Onedio'da. Ayrıntılar için hemen tıkla!",
      Vitrin: "Alışveriş yapmadan bakman gereken $title Onedio'da seni bekliyor!",
      Yazio: "Ufkunu genişletecek $title başlıklı içerik Yazio yazarları kaleminden Onedio'da!",
      Galeriler: "Kimseden geri kalmamak için görmen gereken $title Onedio'da!",
      Goygoy: "Okurken acayip eğleneceğin $title Onedio'da!",
      Gıybet: "Hiçbir dedikodudan geri kalma diye $title detaylarıyla Onedio'da!",
      Spor: "Güncel spor manşetlerinden $title ayrıntılarıyla Onedio'da!",
      Cool: "Okurken 'vay canına!' diyeceğin $title Onedio'da!",
      iq: 'Merak edilen $title konusu hakkında en ilginç detayları öğrenmek için hemen tıkla!',
      Cafe: "Okurken kendinden bir şeyler bulacağın $title içeriği Onedio'da!",
    };
    const description = descriptions[category]?.replace('$title', title) || title;
    return description;
  }
};

export const getArticleMetaTags = (article, isAppView, isBot) => {
  let canonical = false;
  const articleUrl = HOST_URL + getArticleUrl({ article });
  const description = articleDescription(article);
  // Use facebook image uploaded from editors if exists just for og:image
  const ogImageUrl = article.facebookImage ? getImageUrl(article.facebookImage, { raw: true }) : getImageUrl(article.image, { raw: true });
  const metaOpeGraph = getMetaOpenGraph({
    url: articleUrl,
    description,
    imageUrl: ogImageUrl,
    title: article.title,
  });
  const imageUrl = getImageUrl(article.image, { raw: true });
  const metaTwitter = getMetaTwitter({ url: articleUrl, description, imageUrl, title: article.title });
  const meta = [
    //
    { hid: 'description', name: 'description', content: description },
    { hid: 'datePublished', name: 'datePublished', content: dayjs(article.dates?.published).format('YYYY-MM-DDTHH:mm:ssZ') },
    { hid: 'dateModified', name: 'dateModified', content: dayjs(article.dates?.modified).format('YYYY-MM-DDTHH:mm:ssZ') },
    { hid: 'author', name: 'author', content: article.authors[0]?.name || '' },
    { hid: 'articleAuthor', name: 'articleAuthor', content: article.authors[0]?.name || '' },
    { hid: 'articleSection', name: 'articleSection', content: article.categories[0]?.name || '' },
    ...metaOpeGraph,
    ...metaTwitter,
  ];

  if (isAppView || article.flags?.isHidden || article.flags?.isDraft || article.flags?.isDeleted || article.flags?.noindex) {
    meta.push({ hid: 'robots', name: 'robots', content: 'noindex' });
  } else {
    meta.push({ hid: 'robots', name: 'robots', content: 'max-image-preview:large' });
    canonical = true;
  }

  return {
    meta,
    ...getLinkMeta(article, canonical),
  };
  //
};

export const getArticleWordCount = (article) => {
  // article body
  let articleBody = '';
  article.entries?.map((entry) => {
    if (entry?.text?.plain) {
      articleBody += entry.text.plain;
    }
  });
  return articleBody.split(' ').length;
};

// change url when article visible on screen
export const changeHistory = (article) => {
  const articleUrl = getArticleUrl({ article });
  if (document.location.pathname !== articleUrl) {
    history.pushState({}, article?.title, articleUrl);
    document.title = article?.title || i18n.messages[LANG]['news-page']['no-content'];
  }
};

// check has article category name
export const hasArticleCategory = (article, category) => {
  if (category === 'Video' && article?.flags) {
    return article?.flags?.isVideo || false;
  }
  return article?.categories?.find((cat) => cat.name === category);
};

// check if video of onedio exist or not
export const hasOnedioVideo = (article) => {
  return (article?.value ?? article).entries?.some((entry) => entry.videoData?.source === 'onedio');
};

// decide new video shown in article page
export const isVideoArticle = (article) => {
  const hasVideoPageDesignFlag = (article?.value ?? article)?.flags?.showVideoPageDesign;
  const sponsored = SPONSORED.some((i) => (article?.value ?? article)?.tags?.includes(i));
  const isOnedioVideo = (article?.value ?? article).entries?.some((entry) => entry.videoData?.source === 'onedio');
  return isOnedioVideo && !sponsored && hasVideoPageDesignFlag;
};

export const getArticleCategoryPathNames = (article) => {
  const categoriesById = {};
  const categoriesArray = [];
  article?.categories?.forEach((category, index) => {
    categoriesById[category._id] = category;
    if (!category.parent) return categoriesArray.push(category.name);
    if (!article.categories[index - 1]) return;
    if (category.parent !== article.categories[index - 1]._id) {
      const parentCat = categoriesById[category.parent]?.parent;
      categoriesArray.push(
        parentCat ? `${categoriesById[parentCat].name}/${categoriesById[category.parent].name}` : categoriesById[category.parent].name
      );
    }
    categoriesArray[categoriesArray.length - 1] += `/${category.name}`;
  });
  return categoriesArray;
};
